import { Fragment, ReactElement, useMemo } from 'react';

import {
  FilterAppliedProps,
  FilterComparer,
  FilterConfig,
  Filters,
  useCurrentFilter,
} from '@components/filters';
import { IconProps } from '@components/icons';
import { IconApplicants } from '@components/icons/applicants';
import { IconArrowLeft } from '@components/icons/arrow-left';
import { IconEmail } from '@components/icons/email';
import { IconFile } from '@components/icons/file';
import { IconMessages } from '@components/icons/messages';
import { IconNotes } from '@components/icons/notes';
import { IconPhone } from '@components/icons/phone';
import { IconRadioChecked } from '@components/icons/radio-checked';
import { IconRadioIdle } from '@components/icons/radio-idle';
import { TextField } from '@components/text-field';

import { useStore } from 'models/Provider';

import { FilterActionButtons } from './filter-action-buttons';

const FIELDS = [
  { key: 'name', icon: <IconApplicants /> },
  { key: 'email', icon: <IconEmail /> },
  { key: 'phone', icon: <IconPhone /> },
  { key: 'cv', icon: <IconFile /> },
  { key: 'cover_letter', icon: <IconFile /> },
  { key: 'notes', icon: <IconNotes /> },
  { key: 'messages', icon: <IconMessages /> },
] as Array<{ key: string; icon: ReactElement<IconProps> }>;

const COMPARERS: Record<(typeof FIELDS)[number]['key'], Array<FilterComparer>> = {
  name: ['is', 'not_is', 'starts'],
  email: ['is', 'not_is', 'contains', 'not_contains'],
  phone: ['is', 'not_is', 'contains', 'not_contains'],
  cv: ['contains', 'not_contains'],
  cover_letter: ['contains', 'not_contains'],
  notes: ['contains', 'not_contains'],
  messages: ['contains', 'not_contains'],
};

function FilterApplicationPersonalDetailsApplied({ data }: FilterAppliedProps) {
  const { t } = useStore();

  const valueLabel = Array.isArray(data.value) ? data.value.join(', ') : (data.value as string);

  return (
    <Filters.AppliedFilter
      icon={<IconApplicants />}
      category={t('applicants.filters.personal.' + data.field)}
      value={valueLabel}
      comparer={{
        value: data.comparer!,
        label: t('comparers.' + data.comparer).replace('...', ''),
      }}
    />
  );
}

function FilterApplicationPersonalDetailsMenu() {
  const { t } = useStore();
  const { state, actions, hasValue } = useCurrentFilter();

  return (
    <Filters.List>
      <Filters.ListItem
        heading
        label={t('applicants.filters.personal.title')}
        icon={<IconArrowLeft />}
        onClick={actions.unselectCategory}
      />

      {state.field && (
        <Filters.ListItem
          heading
          label={t('applicants.filters.personal.' + state.field)}
          icon={<IconArrowLeft />}
          onClick={actions.unselectField}
        />
      )}

      <Filters.ListSeparator />

      {!state.field && (
        <Fragment>
          {FIELDS.map((field) => (
            <Filters.ListItem
              key={field.key}
              icon={field.icon}
              label={t('applicants.filters.personal.' + field.key)}
              onClick={() => actions.selectField(field.key)}
            />
          ))}
        </Fragment>
      )}

      {state.field && (
        <Fragment>
          {COMPARERS[state.field].map((comparer) => {
            const isCurrentComparer = state.comparer === comparer;
            const label = t('comparers.' + comparer).replace('...', '');
            return (
              <Fragment key={comparer}>
                <Filters.ListItem
                  icon={isCurrentComparer ? <IconRadioChecked /> : <IconRadioIdle />}
                  key={comparer}
                  label={label}
                  onClick={() => actions.selectComparer(comparer)}
                />
                {isCurrentComparer && (
                  <TextField
                    autoFocus
                    onChange={actions.selectValue}
                    defaultValue={state.value as string}
                    label={label}
                    hideLabel
                  />
                )}
              </Fragment>
            );
          })}
          <FilterActionButtons disableApply={!hasValue} />
        </Fragment>
      )}
    </Filters.List>
  );
}

export function useFilterApplicationPersonalDetails(): FilterConfig {
  const { t } = useStore();

  return useMemo(
    () => ({
      category: 'personal',
      label: t('applicants.filters.personal.title'),
      icon: <IconApplicants />,
      AppliedFilter: FilterApplicationPersonalDetailsApplied,
      Menu: FilterApplicationPersonalDetailsMenu,
      toQueryData: (filter) => {
        const field = filter.field as string;
        const value = filter.value as string;

        switch (filter.comparer) {
          case 'is':
            return { [`${field}__is`]: value.toLowerCase().trim() };
          case 'starts':
            return { [`${field}__starts_with`]: value.toLowerCase().trimStart() };
          case 'not_is':
            return { [`${field}__is_not`]: value.toLowerCase().trim() };
          case 'contains':
            return { [`${field}__wildcard`]: value.toLowerCase().trimStart() };
          case 'not_contains':
            return { [`${field}__not_contain`]: value.toLowerCase().trimStart() };
          default:
            return {};
        }
      },
    }),
    [t]
  );
}
